import React, { useCallback, useRef, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ReplayIcon from '@mui/icons-material/Replay';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import CustomButton from 'components/button/CustomButton';

import CsvPreviewTable from './CsvPreviewTable';
import { parseCsvFile, validateCsvData } from './csvUtils';
import { BulkRegisterRow, CsvBulkConfig, CsvParsedRow } from './types';

type CsvBulkUploadDialogProps<TParams> = {
  /** ダイアログ開閉状態 */
  open: boolean;
  /** ダイアログを閉じるハンドラ */
  onClose: () => void;
  /** CSV一括登録設定 */
  config: CsvBulkConfig<TParams>;
  /** 登録完了後のコールバック */
  onComplete?: () => void;
};

type Phase = 'upload' | 'preview' | 'registering' | 'done';

/**
 * CSV一括登録ダイアログ
 * アップロード → プレビュー → 登録処理 → 完了 のフローを管理する
 */
const CsvBulkUploadDialog = <TParams,>({
  open,
  onClose,
  config,
  onComplete,
}: CsvBulkUploadDialogProps<TParams>): React.ReactElement => {
  const [phase, setPhase] = useState<Phase>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [parsedRows, setParsedRows] = useState<CsvParsedRow[]>([]);
  const [bulkRows, setBulkRows] = useState<BulkRegisterRow[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasAllSuccess = parsedRows.length > 0 && parsedRows.every((r) => r.status === 'success');
  const hasRegisterErrors = bulkRows.some((r) => r.registerStatus === 'error');
  const allRegistered =
    bulkRows.length > 0 &&
    bulkRows.every((r) => r.registerStatus === 'success' || r.registerStatus === 'error');
  const allRegisterSuccess =
    bulkRows.length > 0 && bulkRows.every((r) => r.registerStatus === 'success');

  const resetState = useCallback(() => {
    setPhase('upload');
    setFile(null);
    setParsedRows([]);
    setBulkRows([]);
    setErrorMessage('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setErrorMessage('');
    }
  };

  const handleParse = async () => {
    if (!file) return;

    setErrorMessage('');
    try {
      const data = await parseCsvFile(file);
      if (data.length === 0) {
        setErrorMessage('CSVファイルにデータがありません');
        return;
      }
      const validated = validateCsvData(data, config);
      setParsedRows(validated);
      setPhase('preview');
    } catch (e: unknown) {
      setErrorMessage(e instanceof Error ? e.message : 'CSVの読み込みに失敗しました');
    }
  };

  const handleCancel = () => {
    resetState();
  };

  const handleBulkRegister = async () => {
    setPhase('registering');

    // BulkRegisterRow初期化
    const initial: BulkRegisterRow[] = parsedRows.map((row) => ({
      ...row,
      registerStatus: 'pending',
    }));
    setBulkRows(initial);

    // 1件ずつ登録
    const results = [...initial];
    for (let i = 0; i < results.length; i++) {
      results[i] = { ...results[i], registerStatus: 'processing' };
      setBulkRows([...results]);

      try {
        const params = config.toApiParams(results[i].data);
        await config.registerOne(params);
        results[i] = { ...results[i], registerStatus: 'success' };
      } catch (e: unknown) {
        const errorMsg = e instanceof Error ? e.message : '登録に失敗しました';
        results[i] = { ...results[i], registerStatus: 'error', registerError: errorMsg };
      }
      setBulkRows([...results]);
    }

    setPhase('done');
  };

  const handleRetry = async () => {
    setPhase('registering');

    const results = [...bulkRows];
    for (let i = 0; i < results.length; i++) {
      if (results[i].registerStatus !== 'error') continue;

      results[i] = { ...results[i], registerStatus: 'processing', registerError: undefined };
      setBulkRows([...results]);

      try {
        const params = config.toApiParams(results[i].data);
        await config.registerOne(params);
        results[i] = { ...results[i], registerStatus: 'success' };
      } catch (e: unknown) {
        const errorMsg = e instanceof Error ? e.message : '登録に失敗しました';
        results[i] = { ...results[i], registerStatus: 'error', registerError: errorMsg };
      }
      setBulkRows([...results]);
    }

    setPhase('done');
  };

  const handleDone = () => {
    resetState();
    onClose();
    onComplete?.();
  };

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={phase === 'registering' ? undefined : handleClose}
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 3,
          border: 1,
          borderColor: 'divider',
          minHeight: phase === 'upload' ? undefined : '70vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {config.entityName} 一括登録
        </Typography>
        {phase !== 'registering' && (
          <IconButton size="small" onClick={handleClose} sx={{ color: 'text.secondary' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Stack spacing={2}>
            {/* アップロードフェーズ */}
            {phase === 'upload' && (
              <>
                <Typography variant="body2" color="text.secondary">
                  CSVファイルを選択して読み込みボタンを押してください。
                </Typography>
                <Box
                  sx={{
                    border: 2,
                    borderStyle: 'dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    transition: 'border-color 0.2s',
                    '&:hover': { borderColor: 'primary.main' },
                  }}
                >
                  <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    CSVファイルを選択してください
                  </Typography>
                  <Button
                    variant="outlined"
                    component="label"
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                  >
                    ファイルを選択
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      hidden
                      onChange={handleFileChange}
                    />
                  </Button>
                  {file && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      選択中: {file.name}
                    </Typography>
                  )}
                </Box>
                {errorMessage && (
                  <Alert severity="error" sx={{ borderRadius: 2 }}>
                    {errorMessage}
                  </Alert>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <CustomButton
                    label="読み込み"
                    buttonVariant="primary"
                    icon={<PlayArrowIcon />}
                    onClick={handleParse}
                    disabled={!file}
                  />
                </Box>
              </>
            )}

            {/* プレビューフェーズ */}
            {phase === 'preview' && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {parsedRows.length}件のデータを読み込みました。
                  </Typography>
                  {!hasAllSuccess && (
                    <Typography variant="body2" color="error.main">
                      エラーのある行を修正してください。
                    </Typography>
                  )}
                </Box>
                <CsvPreviewTable
                  rows={parsedRows}
                  fields={config.fields}
                  resolveDisplayValue={config.resolveDisplayValue}
                />
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <CustomButton
                    label="キャンセル"
                    buttonVariant="secondary"
                    onClick={handleCancel}
                  />
                  <CustomButton
                    label="一括登録"
                    buttonVariant="primary"
                    onClick={handleBulkRegister}
                    disabled={!hasAllSuccess}
                  />
                </Stack>
              </>
            )}

            {/* 登録処理中・完了フェーズ */}
            {(phase === 'registering' || phase === 'done') && (
              <>
                <Typography variant="body2" color="text.secondary">
                  {phase === 'registering'
                    ? '登録処理中...'
                    : allRegisterSuccess
                      ? '全ての登録が完了しました。'
                      : '一部の登録に失敗しました。'}
                </Typography>
                <CsvPreviewTable
                  rows={bulkRows}
                  fields={config.fields}
                  resolveDisplayValue={config.resolveDisplayValue}
                  isRegistering={true}
                />
                {phase === 'done' && (
                  <Stack direction="row" spacing={2} justifyContent="flex-end">
                    {allRegistered && !hasRegisterErrors && (
                      <CustomButton
                        label="完了"
                        buttonVariant="success"
                        icon={<DoneAllIcon />}
                        onClick={handleDone}
                      />
                    )}
                    {allRegistered && hasRegisterErrors && (
                      <>
                        <CustomButton
                          label="キャンセル"
                          buttonVariant="secondary"
                          onClick={handleClose}
                        />
                        <CustomButton
                          label="再実行"
                          buttonVariant="primary"
                          icon={<ReplayIcon />}
                          onClick={handleRetry}
                        />
                      </>
                    )}
                  </Stack>
                )}
              </>
            )}
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CsvBulkUploadDialog;
